import React, { ReactNode } from 'react';

import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { PencilIcon } from '@zola/zola-ui/src/components/SvgIconsV3/Pencil';
import COLORS3 from '@zola/zola-ui/src/styles/emotion/colors3';
import {
  desktopV2Only,
  excludeDesktopV2,
} from '@zola/zola-ui/src/styles/emotion/mixins/responsiveV2';
import SPACING from '@zola/zola-ui/src/styles/emotion/spacing';
import H from '@zola/zola-ui/src/typography/Headings';

import styled from '@emotion/styled';

import EditLocationModal from '~/pages/couples/explore/components/SearchBar/EditLocationModal';
import { LocationOrAllMarketsType } from '~/types/responseTypes';

const Hed = styled(H.TitleMedium1)`
  display: inline;
`;

const ClickableSpan = styled.span`
  text-decoration: underline;
  text-underline-offset: 3px;
  &:active,
  &:hover {
    color: ${COLORS3.BLACK_075};
  }
`;

const NoWrap = styled.span`
  white-space: nowrap;
  display: inline-block;
  text-decoration: underline;
`;

const EditLocation = styled(LinkV2)`
  margin-left: ${SPACING.XS};
  vertical-align: bottom;
`;

type EditLocationButtonProps = {
  ariaHidden?: boolean;
  editLocationAriaLabel?: string;
  onLocationClick: () => void;
};

const EditLocationButton = ({
  ariaHidden = false,
  editLocationAriaLabel = '',
  onLocationClick,
}: EditLocationButtonProps) => (
  <EditLocation
    aria-hidden={ariaHidden}
    aria-label={editLocationAriaLabel}
    role="button"
    onClick={onLocationClick}
    tabIndex={ariaHidden ? -1 : 0}
  >
    <PencilIcon height={20} width={20} title="" />
  </EditLocation>
);

type SharedPageTitleProps = {
  category: string;
  description: ReactNode;
  formattedCount?: ReactNode;
  location?: string;
  onCategoryClick: () => void;
  onLocationClick: () => void;
  onUpdateSelectedLocation: (location: LocationOrAllMarketsType) => void;
  setShowLocationModal: (show: boolean) => void;
  showLocationModal: boolean;
};

/**
 * SRP page title shared between pre-auth and post-auth implementations
 * - Pre-auth (~/pages/couples/explore/components/SearchResults/components/PreAuth/PageHeading)
 * - Post-auth (~/pages/couples/explore/components/SearchResults/components/PageTitle)
 */
export const SharedPageTitle = ({
  category,
  description,
  formattedCount,
  location = '',
  onCategoryClick,
  onLocationClick,
  setShowLocationModal,
  showLocationModal,
  onUpdateSelectedLocation,
}: SharedPageTitleProps) => {
  const editLocationAriaLabel = `Edit search location ${location}`;
  const locationParts = location.split(' ');

  return (
    <>
      <div css={desktopV2Only}>
        {formattedCount}
        <Hed presentation="h4">
          {description} {category}
          {location ? ` in ${location}` : ''}
        </Hed>
      </div>
      <div css={excludeDesktopV2}>
        {formattedCount}
        <Hed presentation="h4">
          <ClickableSpan
            aria-label={`Edit search category ${category}`}
            onClick={onCategoryClick}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onCategoryClick();
            }}
            role="button"
            tabIndex={0}
          >
            <NoWrap>
              {description}&nbsp;{category}
            </NoWrap>
          </ClickableSpan>
          {location ? (
            <>
              &nbsp;in&nbsp;
              <ClickableSpan
                aria-label={editLocationAriaLabel}
                onClick={onLocationClick}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onLocationClick();
                }}
                role="button"
                tabIndex={0}
              >
                {locationParts.slice(0, -1).join(' ')}&nbsp;
                <NoWrap>
                  {locationParts.slice(-1)}
                  <EditLocationButton ariaHidden onLocationClick={onLocationClick} />
                </NoWrap>
              </ClickableSpan>
            </>
          ) : (
            <EditLocationButton
              editLocationAriaLabel={editLocationAriaLabel}
              onLocationClick={onLocationClick}
            />
          )}
        </Hed>
        {showLocationModal && (
          <EditLocationModal
            hideModalFn={() => setShowLocationModal(false)}
            onSelect={onUpdateSelectedLocation}
          />
        )}
      </div>
    </>
  );
};
